import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { StateService } from '@uirouter/core';
import { SessionService } from '@monsido/core/session/session.service';
import { LocaleService, LocaleOptionType } from '@client/app/shared-modules/api/services/locale.service';
import { MeRepo } from '@client/app/feature-modules/navigation/repos/me.repo';
import { LoginService } from '@client/app/core/services/login/login.service';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { MonTranslationService } from '@monsido/core/translation/translation.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { LanguageType } from '@monsido_services/services/mon-translate/dist/translate.interface';
import { EditProfileService, TimezoneOption } from '@client/app/feature-modules/navigation/profile/edit-profile/services/edit-profile.service';
import { ProfileFormData } from '@client/app/feature-modules/navigation/profile/edit-profile/types/profile.type';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import {
    AiRemediationsResponsibility,
    AI_REMEDIATIONS_RESPONSIBILITIES,
} from '@monsido/ng2/modules/models/api/interfaces/ai-remediations-responsibility.type';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';

@Component({
    selector: 'mon-edit-profile',
    standalone: true,
    imports: [
        FormsModule,
        TranslocoModule,
        InputTextModule,
        SelectModule,
        ToggleSwitchModule,
        ButtonModule,
        CardModule,
        ProgressSpinnerModule,
        PageLayoutComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './edit-profile.component.html',
    styleUrls: ['./edit-profile.component.scss'],
})
export class EditProfileComponent implements OnInit {
    readonly isSubmitting = signal(false);
    readonly isLoadingProfile = signal(true);
    readonly aiRemediationActive = signal(false);
    readonly remediationRoles = AI_REMEDIATIONS_RESPONSIBILITIES;

    private readonly currentUser = toSignal(inject(SessionService).meObs);
    readonly is2FAEnabled = computed(() => this.currentUser()?.otp ?? false);

    profileData: ProfileFormData = {
        email: '',
        first_name: '',
        last_name: '',
        locale: '',
        timezone: '',
        email_notifications: { exports: false },
        auth_user_password: '',
        password: '',
        password_confirmation: '',
        accessibility_responsibilities: [],
    };

    organizationName: string = '';
    languages: LocaleOptionType[] = [];
    timezones: TimezoneOption[] = [];

    constructor (
        private readonly sessionService: SessionService,
        private readonly localeService: LocaleService,
        private readonly editProfileService: EditProfileService,
        private readonly meRepo: MeRepo,
        private readonly loginService: LoginService,
        private readonly toastService: GlobalToastMessageService,
        private readonly translationService: MonTranslationService,
        private readonly dateTimeService: DateTimeService,
        private readonly stateService: StateService,
        private readonly translocoService: TranslocoService,
        private readonly activeFeatureService: ActiveFeatureService,
    ) {}

    ngOnInit (): void {
        this.aiRemediationActive.set(this.activeFeatureService.isFeatureActivePure('ai_remediation'));
        this.languages = this.localeService.getLocaleOptions();
        this.timezones = this.editProfileService.getTimezones();
        this.loadProfile();
    }

    onBack (): void {
        this.stateService.go('base.customer.dashboard');
    }

    onCancel (): void {
        this.stateService.go('base.customer.dashboard');
    }

    setup2FA (): void {
        this.loginService.setup2FA();
    }

    async onSubmit (form: NgForm): Promise<void> {
        if (this.isSubmitting()) {
            return;
        }

        if (this.profileData.password || this.profileData.password_confirmation) {
            if (this.profileData.password !== this.profileData.password_confirmation) {
                this.toastService.error(this.translocoService.translate('editProfile.error.passwordMismatch'));
                return;
            }
        }

        if (form.invalid) {
            form.control.markAllAsTouched();
            this.toastService.error(this.translocoService.translate('editProfile.error.requiredFields'));
            return;
        }

        this.isSubmitting.set(true);

        const prevLocale = this.currentUser()?.locale ?? '';
        const newLocale = this.profileData.locale;

        const payload = this.editProfileService.buildUpdatePayload(this.profileData);

        try {
            await this.meRepo.update(payload);

            const sessionUser = this.sessionService.me;
            if (sessionUser) {
                const sessionPatch: Partial<typeof sessionUser> = {
                    first_name: this.profileData.first_name,
                    last_name: this.profileData.last_name,
                    timezone: this.profileData.timezone,
                    email_notifications: this.profileData.email_notifications,
                    ...(this.profileData.locale
                        ? { locale: this.profileData.locale as NonNullable<typeof sessionUser.locale> }
                        : {}),
                };

                if (sessionUser.settings) {
                    sessionPatch.settings = {
                        ...sessionUser.settings,
                        accessibility_responsibilities: [...this.profileData.accessibility_responsibilities],
                    };
                }

                Object.assign(sessionUser, sessionPatch);
                this.sessionService.setMe(sessionUser);
            }

            if (newLocale && newLocale !== prevLocale) {
                this.translationService.setLanguage(newLocale as LanguageType);
                this.dateTimeService.setLocale(newLocale);
            }

            this.toastService.success(this.translocoService.translate('editProfile.success.saved'));
            this.stateService.go('base.customer.dashboard');
        } catch {
            // REDESIGN_ERROR_ALERT context token in repository handles API errors via redesigned dialogs.
        } finally {
            this.isSubmitting.set(false);
        }
    }

    private loadProfile (): void {
        const user = this.sessionService.me;
        if (user) {
            const profileState = this.editProfileService.toProfileState(user);
            this.profileData = profileState.profileData;
            this.organizationName = profileState.organizationName;
        }
        this.isLoadingProfile.set(false);
    }

    isResponsibilitySelected (role: AiRemediationsResponsibility): boolean {
        return this.profileData.accessibility_responsibilities.includes(role);
    }

    onResponsibilityToggle (role: AiRemediationsResponsibility, checked: boolean): void {
        const current = this.profileData.accessibility_responsibilities;
        const hasRole = current.includes(role);

        if (checked && !hasRole) {
            this.profileData.accessibility_responsibilities = [...current, role];
            return;
        }

        if (!checked && hasRole) {
            this.profileData.accessibility_responsibilities = current.filter(existingRole => existingRole !== role);
        }
    }

    getResponsibilityTranslationKey (role: AiRemediationsResponsibility): string {
        const responsibilityLabels: Record<AiRemediationsResponsibility, string> = {
            'Visual Design': 'editProfile.remediation.roles.visualDesign',
            'UX Design': 'editProfile.remediation.roles.uxDesign',
            'Content Authoring': 'editProfile.remediation.roles.contentAuthoring',
            'Front-end Development': 'editProfile.remediation.roles.frontendDevelopment',
        };

        return responsibilityLabels[role];
    }
}
